import React, { useState, useEffect, useContext } from 'react';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { confirmDialog, notify, reportLoadError } from '../utils/notify';
import { can } from '../utils/permissions';
import TaxYearSimulation from './TaxYearSimulation';
import { ChartColumn, FlaskConical, NotebookPen, Save, Calculator, TriangleAlert } from 'lucide-react';

// The tax year of Tax Config: which year is shown, "copy <last> to <next>"
// (Loonbelasting parameters, brackets and exemptions together, never over
// an existing year), the Loonbelasting parameters of the year and the test
// calculator, and the simulation of the parameters on the year's last
// approved payroll. Payroll uses the year its period ends in. It came from
// Settings; onYearChange tells Tax Config which year's brackets to edit.
const TaxYearSection = ({ onYearChange }) => {
  const { t, locale } = useLanguage();
  const { user } = useContext(AuthContext) || {};
  // Loonbelasting: tax_configuration (copying a year creates one).
  const canCopyLb = can(user, 'tax_configuration', 'create');
  const canEditLb = can(user, 'tax_configuration', 'update');
  const [loading, setLoading] = useState(false);

  // ========== LOONBELASTING CONFIG STATE ==========
  // One configuration per year, all from the server: nothing is assumed.
  // A save changes only the year shown; a new year is copied from another.
  const [lbYears, setLbYears] = useState([]);
  const [lbCurrentYear, setLbCurrentYear] = useState(null);
  const [loonbelastingConfig, setLoonbelastingConfig] = useState(null);

  const [testResult, setTestResult] = useState(null);
  const [testGrossPay, setTestGrossPay] = useState('18000');

  // ========== LOONBELASTING FUNCTIONS ==========
  const loadLoonbelastingYears = async (selectYear) => {
    try {
      const response = await api.get('/settings/loonbelasting/years');
      const { years, current_year } = response.data;
      setLbYears(years);
      setLbCurrentYear(current_year);
      setLoonbelastingConfig(
        years.find(y => y.config_year === selectYear)
        || years.find(y => y.config_year === current_year)
        || years[years.length - 1]
        || null
      );
    } catch (error) {
      console.error('Error loading Loonbelasting config:', error);
      reportLoadError(error, t('Error loading Loonbelasting configuration'));
    }
  };

  const selectLbYear = (year) => {
    setLoonbelastingConfig(lbYears.find(y => y.config_year === year) || null);
    setTestResult(null);
  };

  const copyLbYear = async (fromYear, toYear) => {
    if (!await confirmDialog(t('Create {to} with the Loonbelasting parameters, brackets and exemptions of {from}?', { from: fromYear, to: toYear }))) return;
    try {
      setLoading(true);
      await api.post('/settings/loonbelasting/copy', { from_year: fromYear, to_year: toYear });
      notify(t('{year} created. Check its values and save.', { year: toYear }), 'success');
      await loadLoonbelastingYears(toYear);
    } catch (error) {
      console.error('Error copying Loonbelasting config:', error);
      notify(error.response?.data?.message || t('Failed to save configuration'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const saveLoonbelastingConfig = async () => {
    const fields = ['forfait_percentage', 'forfait_max_amount', 'tax_free_amount', 'aov_percentage'];
    if (fields.some(f => loonbelastingConfig[f] === '' || !Number.isFinite(Number(loonbelastingConfig[f])))) {
      notify(t('Fill in every amount before saving'), 'error');
      return;
    }
    try {
      setLoading(true);
      const year = loonbelastingConfig.config_year;
      await api.put('/settings/loonbelasting', {
        config_year: year,
        forfait_percentage: Number(loonbelastingConfig.forfait_percentage),
        forfait_max_amount: Number(loonbelastingConfig.forfait_max_amount),
        tax_free_amount: Number(loonbelastingConfig.tax_free_amount),
        aov_percentage: Number(loonbelastingConfig.aov_percentage),
        notes: loonbelastingConfig.notes || ''
      });
      notify(t('Loonbelasting {year} saved', { year }), 'success');
      await loadLoonbelastingYears(year);
    } catch (error) {
      console.error('Error saving Loonbelasting config:', error);
      notify(error.response?.data?.message || t('Failed to save configuration'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const testLoonbelastingCalc = async () => {
    try {
      if (!testGrossPay || parseFloat(testGrossPay) <= 0) {
        notify(t('Please enter a valid gross pay amount'), 'error');
        return;
      }

      setLoading(true);
      const response = await api.get('/settings/loonbelasting/test', {
        params: { 
          grossPay: testGrossPay,
          year: loonbelastingConfig?.config_year
        }
      });
      
      if (response.data.success) {
        setTestResult(response.data.calculation);
      }
    } catch (error) {
      console.error('Error testing calculation:', error);
      notify(t('Error: {message}', { message: error.response?.data?.message || t('Failed to test calculation') }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLoonbelastingYears();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedYear = loonbelastingConfig?.config_year || null;
  useEffect(() => {
    if (onYearChange) onYearChange(selectedYear);
  }, [selectedYear, onYearChange]);

  return (
    <>
        {/* ========== LOONBELASTING CONFIGURATION ========== */}
        <div className="card tc-panel">
          <h2 className="tc-panel__title">{t('Loonbelasting Configuration (Suriname Wage Tax)')}</h2>
          <p className="tc-lead">
            {t("Configure parameters for Suriname's progressive wage tax calculation (Year {year})", { year: loonbelastingConfig?.config_year || '—' })}
          </p>

          {/* One configuration per year; payroll uses the year of the period's end. */}
          <div className="tc-year-bar">
            <label htmlFor="lb-year" className="field-label">{t('Year')}</label>
            <select className="select"
              id="lb-year"
              value={loonbelastingConfig?.config_year || ''}
              onChange={(e) => selectLbYear(Number(e.target.value))}
              disabled={lbYears.length === 0}
            >
              {lbYears.map(y => (
                <option key={y.config_year} value={y.config_year}>{y.config_year}</option>
              ))}
            </select>
            {canCopyLb && lbYears.length > 0 && (() => {
              const last = lbYears[lbYears.length - 1].config_year;
              return (
                <button type="button" className="btn btn--secondary" onClick={() => copyLbYear(last, last + 1)} disabled={loading}>
                  {t('Copy {from} to {to}', { from: last, to: last + 1 })}
                </button>
              );
            })()}
          </div>
          {lbCurrentYear && lbYears.length > 0 && [lbCurrentYear, lbCurrentYear + 1]
            .filter(year => !lbYears.some(y => y.config_year === year))
            .map(year => (
              <div key={year} className={`tc-alert${year === lbCurrentYear ? ' is-urgent' : ''}`} role="alert">
                <TriangleAlert aria-hidden="true" /> {year === lbCurrentYear
                  ? t('There is no configuration for {year}: payroll for periods ending in {year} cannot be calculated. Copy the last year to create it.', { year })
                  : t('There is no configuration for {year} yet: create it before January.', { year })}
              </div>
            ))}

          {!loonbelastingConfig ? (
            <p className="tc-lead">{lbYears.length === 0 && lbCurrentYear ? t('No Loonbelasting configuration exists.') : t('Loading...')}</p>
          ) : (
          <div className="tc-lb-config">
            {/* Forfait Aftrek */}
            <div className="tc-group">
              <h3 className="tc-group__title"><span className="tc-step">1</span> {t('Forfaitaire Aftrek (Forfait Deduction)')}</h3>
              <div className="tc-form-row">
                <div className="tc-field">
                  <label className="field-label">{t('Forfait Percentage (%)')} *</label>
                  <input className="input"
                    type="number"
                    value={loonbelastingConfig.forfait_percentage}
                    onChange={(e) => setLoonbelastingConfig({
                      ...loonbelastingConfig,
                      forfait_percentage: e.target.value
                    })}
                    step="0.01"
                    min="0"
                    max="100"
                  />
                  <small className="field-hint">{t('Percentage of gross salary (default: 4%)')}</small>
                </div>
                <div className="tc-field">
                  <label className="field-label">{t('Maximum Forfait Amount (SRD)')} *</label>
                  <input className="input"
                    type="number"
                    value={loonbelastingConfig.forfait_max_amount}
                    onChange={(e) => setLoonbelastingConfig({
                      ...loonbelastingConfig,
                      forfait_max_amount: e.target.value
                    })}
                    step="0.01"
                    min="0"
                  />
                  <small className="field-hint">{t('Maximum deduction amount (default: 400)')}</small>
                </div>
              </div>
              <div className="tc-formula">
                <strong>{t('Formula')}:</strong> Forfait = MIN({t('Gross Pay')} × {loonbelastingConfig.forfait_percentage}%, {loonbelastingConfig.forfait_max_amount})
              </div>
            </div>

            {/* Tax-Free Amount */}
            <div className="tc-group">
              <h3 className="tc-group__title"><span className="tc-step">2</span> {t('Belastingvrije Som (Tax-Free Amount)')}</h3>
              <div className="tc-field">
                <label className="field-label">{t('Tax-Free Base Amount (SRD)')} *</label>
                <input className="input"
                  type="number"
                  value={loonbelastingConfig.tax_free_amount}
                  onChange={(e) => setLoonbelastingConfig({
                    ...loonbelastingConfig,
                    tax_free_amount: e.target.value
                  })}
                  step="0.01"
                  min="0"
                />
                <small className="field-hint">{t('Income below this amount is not taxed (default: 9,000)')}</small>
              </div>
              <div className="tc-formula">
                <strong>{t('Formula')}:</strong> {t('Taxable Income')} = MAX((Gross - Forfait) - {Number(loonbelastingConfig.tax_free_amount).toLocaleString(locale)}, 0)
              </div>
            </div>

            {/* AOV */}
            <div className="tc-group">
              <h3 className="tc-group__title"><span className="tc-step">3</span> {t('AOV (Old Age Insurance)')}</h3>
              <div className="tc-field">
                <label className="field-label">{t('AOV Percentage (%)')} *</label>
                <input className="input"
                  type="number"
                  value={loonbelastingConfig.aov_percentage}
                  onChange={(e) => setLoonbelastingConfig({
                    ...loonbelastingConfig,
                    aov_percentage: e.target.value
                  })}
                  step="0.01"
                  min="0"
                  max="100"
                />
                <small className="field-hint">{t('Old age insurance percentage (default: 4%)')}</small>
              </div>
              <div className="tc-formula">
                <strong>{t('Formula')}:</strong> AOV = ({t('Gross Pay')} - Forfait) × {loonbelastingConfig.aov_percentage}%
              </div>
            </div>

            {/* Notes */}
            <div className="tc-group">
              <h3 className="tc-group__title"><NotebookPen aria-hidden="true" /> {t('Notes')}</h3>
              <div className="tc-field">
                <textarea className="textarea"
                  value={loonbelastingConfig.notes || ''}
                  onChange={(e) => setLoonbelastingConfig({
                    ...loonbelastingConfig,
                    notes: e.target.value
                  })}
                  rows="3"
                  placeholder={t('Optional notes about this configuration...')}
                />
              </div>
            </div>

            {/* Save Button */}
            {canEditLb && (
            <div className="tc-actions">
              <button
                type="button"
                onClick={saveLoonbelastingConfig}  
                className="btn btn--primary"
                disabled={loading}
              >
                {loading ? t('Saving...') : <><Save aria-hidden="true" /> {t('Save Loonbelasting Configuration')}</>}
              </button>
            </div>
            )}
          </div>
          )}

          {/* Brackets: those of LB_SRD for this year, edited in its card below */}
          <div className="tc-info">
            <h4><ChartColumn aria-hidden="true" /> {t('Progressive Tax Brackets')}</h4>
            <p>{t('The brackets of {year} are those of Loonbelasting (LB_SRD) below: edit them there with this year selected.', { year: loonbelastingConfig?.config_year || '—' })}</p>
          </div>

          {/* Test Calculator */}
          <div className="card tc-panel tc-test">
            <h3 className="tc-panel__title"><FlaskConical aria-hidden="true" /> {t('Test Calculator')}</h3>
            <p>{t('Test the Loonbelasting calculation with a sample gross pay')}</p>
            
            <div className="tc-test__input">
              <div className="tc-field">
                <label className="field-label">{t('Gross Pay (SRD)')}</label>
                <input className="input"
                  type="number"
                  value={testGrossPay}
                  onChange={(e) => setTestGrossPay(e.target.value)}
                  step="0.01"
                  min="0"
                  placeholder={t('Enter gross salary (e.g., 18000)')}
                />
              </div>
              <button 
                type="button"
                onClick={testLoonbelastingCalc} 
                className="btn btn--primary"
                disabled={loading || !testGrossPay}
              >
                {loading ? t('Calculating...') : <><Calculator aria-hidden="true" /> {t('Calculate')}</>}
              </button>
            </div>

            {/* Test Result */}
            {testResult && (
              <div className="tc-result">
                <h4>{t('Calculation Result for SRD {amount}', { amount: parseFloat(testGrossPay).toLocaleString(locale) })}</h4>
                
                <div className="tc-result__table">
                  <div className="tc-result__row">
                    <span className="tc-result__label">{t('Gross Pay')}:</span>
                    <span className="tc-result__value is-strong">SRD {testResult.grossPay.toLocaleString(locale)}</span>
                  </div>
                  
                  <div className="tc-result__row is-minus">
                    <span className="tc-result__label">{t('- Forfait Aftrek ({pct}%, max {max}):', { pct: testResult.forfaitPercentage, max: testResult.forfaitMaxAmount })}</span>
                    <span className="tc-result__value">SRD {testResult.forfaitAftrek.toLocaleString(locale)}</span>
                  </div>
                  
                  <div className="tc-result__row is-subtotal">
                    <span className="tc-result__label">= {t('Loon na Aftrek')}:</span>
                    <span className="tc-result__value is-strong">SRD {testResult.loonNaAftrek.toLocaleString(locale)}</span>
                  </div>
                  
                  <div className="tc-result__row is-minus">
                    <span className="tc-result__label">- {t('Tax-Free Amount')}:</span>
                    <span className="tc-result__value">SRD {testResult.taxFreeAmount.toLocaleString(locale)}</span>
                  </div>
                  
                  <div className="tc-result__row is-subtotal">
                    <span className="tc-result__label">= {t('Belastbaar Inkomen (Taxable)')}:</span>
                    <span className="tc-result__value is-strong">SRD {testResult.belastbaarInkomen.toLocaleString(locale)}</span>
                  </div>
                  
                  <div className="tc-result__divider"></div>
                  
                  <div className="tc-result__row is-tax">
                    <span className="tc-result__label">{t('Loonbelasting (Progressive Tax)')}:</span>
                    <span className="tc-result__value is-tax">SRD {testResult.loonbelasting.toLocaleString(locale)}</span>
                  </div>
                  
                  <div className="tc-result__row is-tax">
                    <span className="tc-result__label">{t('AOV ({pct}%):', { pct: testResult.aovPercentage })}</span>
                    <span className="tc-result__value is-tax">SRD {testResult.aov.toLocaleString(locale)}</span>
                  </div>
                  
                  <div className="tc-result__row is-total">
                    <span className="tc-result__label">{t('Total Deductions')}:</span>
                    <span className="tc-result__value is-strong">SRD {testResult.totalDeductions.toLocaleString(locale)}</span>
                  </div>
                  
                  <div className="tc-result__divider"></div>
                  
                  <div className="tc-result__row is-net">
                    <span className="tc-result__label">{t('Net Pay')}:</span>
                    <span className="tc-result__value is-net">SRD {testResult.netPay.toLocaleString(locale)}</span>
                  </div>
                  
                  <div className="tc-result__row is-info">
                    <span className="tc-result__label">{t('Effective Tax Rate')}:</span>
                    <span className="tc-result__value">{testResult.effectiveTaxRate}%</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* The parameters on screen on the last approved payroll (read-only) */}
          {canEditLb && loonbelastingConfig && (
            <TaxYearSimulation year={loonbelastingConfig.config_year} draft={loonbelastingConfig} />
          )}
        </div>
    </>
  );
};

export default TaxYearSection;
